import React from 'react';
import { CodeBox, EndpointBar } from './CodeBox';

export const Content = (handleCopy) => {
  const copyText = (text) => {
    handleCopy(text);
  };

  // Render a JSON string into the line array the dark CodeBox expects. Used by
  // the data-driven endpoint sections below so each one only needs its example
  // JSON once (for both display and copy) instead of a hand-built line list.
  const jsonLines = (json) =>
    json
      .trim()
      .split('\n')
      .map((text) => ({ className: 'textStyle4', text }));

  // A single endpoint reference section. Keeps the newer endpoints consistent
  // with the original hand-written ones (same CodeBox, same headings) without
  // repeating ~70 lines of boilerplate each.
  const EndpointSection = ({
    description,
    method = 'GET',
    endpoint,
    params = [],
    example,
    responseJson,
    notes,
  }) => (
    <div className='d-flex flex-column'>
      {typeof description === 'string' ? (
        <p className='p-0 m-0'>{description}</p>
      ) : (
        description
      )}
      <EndpointBar
        isArray={false}
        wrapperClass='d-flex flex-row align-items-center'
        insideRequest={method}
        insideText={endpoint}
        handleCopy={() => copyText(endpoint)}
      />
      {params.length > 0 && (
        <>
          <h3 className='mt-3'>Parameters</h3>
          <table className='param-table'>
            <thead>
              <tr>
                <th>Name</th>
                <th>Required</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {params.map((p, i) => (
                <tr key={i}>
                  <td>
                    <code>{p.name}</code>
                  </td>
                  <td>
                    {p.required ? (
                      <span className='param-table__req'>required</span>
                    ) : (
                      <span className='param-table__opt'>optional</span>
                    )}
                  </td>
                  <td>{p.desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      {notes}
      <h3 className='mt-3'>Request</h3>
      <CodeBox
        isArray={false}
        wrapperClass='d-flex flex-row align-items-center'
        insideRequest={method}
        insideText={example}
        handleCopy={() => copyText(example)}
      />
      <h3 className='mt-3'>Response</h3>
      <CodeBox
        isArray={true}
        arrayToMap={jsonLines(responseJson)}
        wrapperClass='d-flex flex-row align-items-start'
        handleCopy={() => copyText(responseJson)}
      />
    </div>
  );

  const versionParam = {
    name: 'version',
    required: true,
    desc: 'The API version number (e.g. 1).',
  };

  const getDocsEndpoint = {
    number: 1,
    request: 'GET',
    text: '/api/v{version}/Document/Get',
  };

  const getDocsRequest = {
    number: 1,
    request: 'GET',
    text: '/api/v1/Document/Get',
  };

  const getDocsResponse = [
    {
      className: 'textStyle1',
      text: '{',
    },
    {
      className: 'textStyle2',
      text: '"succeeded": true,',
    },
    {
      className: 'textStyle2',
      text: '"message": null,',
    },
    {
      className: 'textStyle2',
      text: '"errors": null,',
    },
    {
      className: 'textStyle2',
      text: '"data": [',
    },
    {
      className: 'textStyle3',
      text: '{',
    },
    {
      className: 'textStyle4',
      text: '"id": "01LVNLRUZ2V4IOEIHYCJDIR3NRIVWLW3RK",',
    },
    {
      className: 'textStyle4',
      text: '"title": "RG 62 - Better disclosure for investors",',
    },
    {
      className: 'textStyle4',
      text: '"status": "Latest",',
    },
    {
      className: 'textStyle4',
      text: '"versionNumber": "-",',
    },
    {
      className: 'textStyle4',
      text: '"versionDate": "2000-07-31T14:00:00Z",',
    },
    {
      className: 'textStyle4',
      text: '"registrationDate": null,',
    },
    {
      className: 'textStyle4',
      text: '"taxonomy": {',
    },
    {
      className: 'textStyle4',
      text: '  "regulator": { "id": "1", "title": "ASIC" },',
    },
    {
      className: 'textStyle4',
      text: '  "regulation-category": { "id": "14", "title": "Regulatory Guides" },',
    },
    {
      className: 'textStyle4',
      text: '  "entity-type": null,',
    },
    {
      className: 'textStyle4',
      text: '  "document-type": null',
    },
    {
      className: 'textStyle4',
      text: '}',
    },
    {
      className: 'textStyle3',
      text: '},',
    },
    {
      className: 'textStyle3',
      text: '// Additional documents...',
    },
    {
      className: 'textStyle2',
      text: ']',
    },
    {
      className: 'textStyle1',
      text: '}',
    },
  ];

  const getDocsResponseCopy = `
  {
    "succeeded": true,
    "message": null,
    "errors": null,
    "data": [
        {
            "id": "01LVNLRUZ2V4IOEIHYCJDIR3NRIVWLW3RK",
            "title": "RG 62 - Better disclosure for investors",
            "status": "Latest",
            "versionNumber": "-",
            "versionDate": "2000-07-31T14:00:00Z",
            "registrationDate": null,
            "taxonomy": {
                "regulator": { "id": "1", "title": "ASIC" },
                "regulation-category": { "id": "14", "title": "Regulatory Guides" },
                "entity-type": null,
                "document-type": null
            }
        },
        // ... other documents ...
    ]
}
  `;

  const getDocIdEndpoint = {
    number: 1,
    request: 'GET',
    text: '/api/v{version}/Document/Get/{id}',
  };

  const getDocIdRequest = {
    number: 1,
    request: 'GET',
    text: '/api/v1/Document/Get/01LVNLRUZ2V4IOEIHYCJDIR3NRIVWLW3RK',
  };

  const getDocIdResponse = [
    {
      className: 'textStyle1',
      text: '{',
    },
    {
      className: 'textStyle2',
      text: '"succeeded": true,',
    },
    {
      className: 'textStyle2',
      text: '"message": null,',
    },
    {
      className: 'textStyle2',
      text: '"errors": null,',
    },
    {
      className: 'textStyle2',
      text: '"data": {',
    },
    {
      className: 'textStyle4',
      text: '"id": "01LVNLRUZ2V4IOEIHYCJDIR3NRIVWLW3RK",',
    },
    {
      className: 'textStyle4',
      text: '"title": "RG 62 - Better disclosure for investors",',
    },
    {
      className: 'textStyle4',
      text: '"status": "Latest",',
    },
    {
      className: 'textStyle4',
      text: '"versionNumber": "-",',
    },
    {
      className: 'textStyle4',
      text: '"versionDate": "2000-07-31T14:00:00Z",',
    },
    {
      className: 'textStyle4',
      text: '"registrationDate": null,',
    },
    {
      className: 'textStyle4',
      text: '"taxonomy": {',
    },
    {
      className: 'textStyle4',
      text: '  "regulator": { "id": "1", "title": "ASIC" },',
    },
    {
      className: 'textStyle4',
      text: '  "regulation-category": { "id": "14", "title": "Regulatory Guides" },',
    },
    {
      className: 'textStyle4',
      text: '  "entity-type": null,',
    },
    {
      className: 'textStyle4',
      text: '  "document-type": null',
    },
    {
      className: 'textStyle4',
      text: '}',
    },
    {
      className: 'textStyle2',
      text: '}',
    },
    {
      className: 'textStyle1',
      text: '}',
    },
  ];

  const getDocIdResponseCopy = `
  {
    "succeeded": true,
    "message": null,
    "errors": null,
    "data": {
      "id": "01LVNLRUZ2V4IOEIHYCJDIR3NRIVWLW3RK",
      "title": "RG 62 - Better disclosure for investors",
      "status": "Latest",
      "versionNumber": "-",
      "versionDate": "2000-07-31T14:00:00Z",
      "registrationDate": null,
      "taxonomy": {
        "regulator": { "id": "1", "title": "ASIC" },
        "regulation-category": { "id": "14", "title": "Regulatory Guides" },
        "entity-type": null,
        "document-type": null
      }
    }
  }
  `;

  const getSectionsEndpoint = {
    number: 1,
    request: 'GET',
    text: '/api/v{version}/Document/GetSections/{id}',
  };

  const getSectionsRequest = {
    number: 1,
    request: 'GET',
    text: '/api/v1/Document/GetSections/01LVNLRUZ2V4IOEIHYCJDIR3NRIVWLW3RK',
  };

  const getSectionsResponse = [
    {
      className: 'textStyle1',
      text: '{',
    },
    {
      className: 'textStyle2',
      text: '"succeeded": true,',
    },
    {
      className: 'textStyle2',
      text: '"message": null,',
    },
    {
      className: 'textStyle2',
      text: '"errors": null,',
    },
    {
      className: 'textStyle2',
      text: '"data": [',
    },
    {
      className: 'textStyle3',
      text: '{',
    },
    {
      className: 'textStyle4',
      text: '"id": "6832",',
    },
    {
      className: 'textStyle4',
      text: '"title": "Regulatory Guide 62 - Better disclosure for investors",',
    },
    {
      className: 'textStyle4',
      text: '"documentId": "01LVNLRUZ2V4IOEIHYCJDIR3NRIVWLW3RK",',
    },
    {
      className: 'textStyle4',
      text: '"documentName": "",',
    },
    {
      className: 'textStyle4',
      text: '"pageId": "bfd5f2d1-1f5b-4d0c-ae32-838a78589823",',
    },
    {
      className: 'textStyle4',
      text: '"pageParentId": "",',
    },
    {
      className: 'textStyle4',
      text: '"tags": ""',
    },
    {
      className: 'textStyle3',
      text: '},',
    },
    {
      className: 'textStyle3',
      text: '// Additional sections...',
    },
    {
      className: 'textStyle2',
      text: ']',
    },
    {
      className: 'textStyle1',
      text: '}',
    },
  ];

  const getSectionsResponseCopy = `
  {
    "succeeded": true,
    "message": null,
    "errors": null,
    "data": [
      {
        "id": "6832",
        "title": "Regulatory Guide 62 - Better disclosure for investors",
        "documentId": "01LVNLRUZ2V4IOEIHYCJDIR3NRIVWLW3RK",
        "documentName": "",
        "pageId": "bfd5f2d1-1f5b-4d0c-ae32-838a78589823",
        "pageParentId": "",
        "tags": ""
      },
      // Additional sections... (no section body — fetch it with Get Section by ID)
    ]
  }
  `;

  const getGlossaryEndpoint = {
    number: 1,
    request: 'GET',
    text: '/api/v{version}/Document/GetGlossary/{id}',
  };

  const getGlossaryRequest = {
    number: 1,
    request: 'GET',
    text: '/api/v1/Document/GetGlossary/01LVNLRU22JU6N2LXRQJC23PCU4SDJCFFH',
  };

  const getGlossaryResponse = [
    {
      className: 'textStyle1',
      text: '{',
    },
    {
      className: 'textStyle2',
      text: '"succeeded": true,',
    },
    {
      className: 'textStyle2',
      text: '"message": null,',
    },
    {
      className: 'textStyle2',
      text: '"errors": null,',
    },
    {
      className: 'textStyle2',
      text: '"data": [',
    },
    {
      className: 'textStyle3',
      text: '{',
    },
    {
      className: 'textStyle4',
      text: '"id": "22",',
    },
    {
      className: 'textStyle4',
      text: '"name": "Financial Sector Collection of Data Act",',
    },
    {
      className: 'textStyle4',
      text: '"content": {',
    },
    {
      className: 'textStyle5',
      text: '"Term": "Meaning in this Document",',
    },
    {
      className: 'textStyle5',
      text: '"APRA": "means the Australian Prudential Regulation Authority.",',
    },
    {
      className: 'textStyle5',
      text: '// Additional glossary terms...,',
    },
    {
      className: 'textStyle4',
      text: '}',
    },
    {
      className: 'textStyle3',
      text: '}',
    },
    {
      className: 'textStyle2',
      text: ']',
    },
    {
      className: 'textStyle1',
      text: '}',
    },
  ];

  const getGlossaryResponseCopy = `
  {
    "succeeded": true,
    "message": null,
    "errors": null,
    "data": [
      {
        "id": "22",
        "name": "Financial Sector Collection of Data Act",
        "content": {
          "Term": "Meaning in this Document",
          "APRA": "means the Australian Prudential Regulation Authority.",
          // Additional glossary terms...
        }
      }
    ]
    // A document with no glossary returns "data": []
  }
  `;

  const getSectionIdEndpoint = {
    number: 1,
    request: 'GET',
    text: '/api/v{version}/Section/Get/{id}',
  };

  const getSectionIdRequest = {
    number: 1,
    request: 'GET',
    text: '/api/v1/Section/Get/66d80f18-b919-4aa4-b22c-0d9d6f34d168',
  };

  const getSectionIdResponse = [
    {
      className: 'textStyle1',
      text: '{',
    },
    {
      className: 'textStyle2',
      text: '"succeeded": true,',
    },
    {
      className: 'textStyle2',
      text: '"message": null,',
    },
    {
      className: 'textStyle2',
      text: '"errors": null,',
    },
    {
      className: 'textStyle2',
      text: '"data": {',
    },
    {
      className: 'textStyle4',
      text: '"id": "7801",',
    },
    {
      className: 'textStyle4',
      text: '"title": "Transactional facilities",',
    },
    {
      className: 'textStyle4',
      text: '"documentId": "01LVNLRUZXFGTAV2EJL5BJCYMRK2YRKX7P",',
    },
    {
      className: 'textStyle4',
      text: '"documentName": "RG 87 - Charitable Schemes and School Enrolment Deposits",',
    },
    {
      className: 'textStyle4',
      text: '"pageId": "66d80f18-b919-4aa4-b22c-0d9d6f34d168",',
    },
    {
      className: 'textStyle4',
      text: '"pageParentId": "aee406f1-7277-4406-bfb0-0b354123c2f5",',
    },
    {
      className: 'textStyle4',
      text: '"tags": "cheque, client, debenture, fundraiser, interest, investment, investment fundraisers, product",',
    },
    {
      className: 'textStyle4',
      text: '"content": "<div><h4>RG 87.85</h4><p>Retail charitable investment fundraisers must only issue debentures and interests to non-associated retail clients ...</p></div>"',
    },
    {
      className: 'textStyle2',
      text: '}',
    },
    {
      className: 'textStyle1',
      text: '}',
    },
  ];

  const getSectionIdResponseCopy = `
  {
    "succeeded": true,
    "message": null,
    "errors": null,
    "data": {
      "id": "7801",
      "title": "Transactional facilities",
      "documentId": "01LVNLRUZXFGTAV2EJL5BJCYMRK2YRKX7P",
      "documentName": "RG 87 - Charitable Schemes and School Enrolment Deposits",
      "pageId": "66d80f18-b919-4aa4-b22c-0d9d6f34d168",
      "pageParentId": "aee406f1-7277-4406-bfb0-0b354123c2f5",
      "tags": "cheque, client, debenture, fundraiser, interest, investment, investment fundraisers, product",
      "content": "<div><h4>RG 87.85</h4><p>Retail charitable investment fundraisers must only issue debentures and interests to non-associated retail clients ...</p></div>"
    }
  }
  `;

  const getRelatedDocsEndpoint = {
    number: 1,
    request: 'GET',
    text: '/api/v{version}/Document/GetRelatedDocuments/{id}',
  };

  const getRelatedDocsRequest = {
    number: 1,
    request: 'GET',
    text: '/api/v1/Document/GetRelatedDocuments/01LVNLRU3GRSRUR4T5HNBZWWAYS7SDAUJD',
  };

  const getRelatedDocsResponse = [
    {
      className: 'textStyle1',
      text: '{',
    },
    {
      className: 'textStyle2',
      text: '"succeeded": true,',
    },
    {
      className: 'textStyle2',
      text: '"message": null,',
    },
    {
      className: 'textStyle2',
      text: '"errors": null,',
    },
    {
      className: 'textStyle2',
      text: '"data": [',
    },
    {
      className: 'textStyle3',
      text: '{',
    },
    {
      className: 'textStyle4',
      text: '"id": "17",',
    },
    {
      className: 'textStyle4',
      text: '"title": "Corporations Regulations Vol 1",',
    },
    {
      className: 'textStyle4',
      text: '"documentParentName": null,',
    },
    {
      className: 'textStyle4',
      text: '"documentChildName": null,',
    },
    {
      className: 'textStyle4',
      text: '"parentId": "01LVNLRU3GRSRUR4T5HNBZWWAYS7SDAUJD",',
    },
    {
      className: 'textStyle4',
      text: '"childId": "01LVNLRUZ2IZDAJCXBNFDKMV2YTEMNUOYJ"',
    },
    {
      className: 'textStyle3',
      text: '},',
    },
    {
      className: 'textStyle3',
      text: '// Additional related documents...',
    },
    {
      className: 'textStyle2',
      text: ']',
    },
    {
      className: 'textStyle1',
      text: '}',
    },
  ];

  const getRelatedDocsResponseCopy = `
  {
    "succeeded": true,
    "message": null,
    "errors": null,
    "data": [
      {
        "id": "17",
        "title": "Corporations Regulations Vol 1",
        "documentParentName": null,
        "documentChildName": null,
        "parentId": "01LVNLRU3GRSRUR4T5HNBZWWAYS7SDAUJD",
        "childId": "01LVNLRUZ2IZDAJCXBNFDKMV2YTEMNUOYJ"
      }
      // Additional related documents... (empty [] if the document has none)
    ]
  }
  `;

  const getRelatedSectionsEndpoint = {
    number: 1,
    request: 'GET',
    text: '/api/v{version}/Section/GetRelatedSections/{id}',
  };

  const getRelatedSectionsRequest = {
    number: 1,
    request: 'GET',
    text: '/api/v1/Section/GetRelatedSections/bbf046cc-a69e-45c8-852e-95f4bb6853c7',
  };

  const getRelatedSectionsResponse = [
    {
      className: 'textStyle1',
      text: '{',
    },
    {
      className: 'textStyle2',
      text: '"succeeded": true,',
    },
    {
      className: 'textStyle2',
      text: '"message": null,',
    },
    {
      className: 'textStyle2',
      text: '"errors": null,',
    },
    {
      className: 'textStyle2',
      text: '"data": [',
    },
    {
      className: 'textStyle3',
      text: '{',
    },
    {
      className: 'textStyle4',
      text: '"id": "8",',
    },
    {
      className: 'textStyle4',
      text: '"title": "Prudential Standard SPS 310 Audit and Related Matters",',
    },
    {
      className: 'textStyle4',
      text: '"mainDocumentId": "01LVNLRUY5NUTLAVNLRBEJFNYRBEAJRINS",',
    },
    {
      className: 'textStyle4',
      text: '"mainPageId": "bbf046cc-a69e-45c8-852e-95f4bb6853c7",',
    },
    {
      className: 'textStyle4',
      text: '"relatedDocumentId": "01LVNLRU4SMWVPOHDE6JFK2K64FGWYBW6C",',
    },
    {
      className: 'textStyle4',
      text: '"relatedPageId": "ec7a71aa-ab0e-4af5-b6df-8b058b00debd",',
    },
    {
      className: 'textStyle4',
      text: '"relatedPageTitle": "Part 1 - Preliminary"',
    },
    {
      className: 'textStyle3',
      text: '},',
    },
    {
      className: 'textStyle3',
      text: '// Additional related sections...',
    },
    {
      className: 'textStyle2',
      text: ']',
    },
    {
      className: 'textStyle1',
      text: '}',
    },
  ];

  const getRelatedSectionsResponseCopy = `
  {
    "succeeded": true,
    "message": null,
    "errors": null,
    "data": [
      {
        "id": "8",
        "title": "Prudential Standard SPS 310 Audit and Related Matters",
        "mainDocumentId": "01LVNLRUY5NUTLAVNLRBEJFNYRBEAJRINS",
        "mainPageId": "bbf046cc-a69e-45c8-852e-95f4bb6853c7",
        "relatedDocumentId": "01LVNLRU4SMWVPOHDE6JFK2K64FGWYBW6C",
        "relatedPageId": "ec7a71aa-ab0e-4af5-b6df-8b058b00debd",
        "relatedPageTitle": "Part 1 - Preliminary"
      }
      // Additional related sections... (empty [] if the section has none)
    ]
  }
  `;

  const getSummaryEndpoint = {
    number: 1,
    request: 'GET',
    text: '/api/v{version}/Document/GetSummary/{id}',
  };

  const getSummaryRequest = {
    number: 1,
    request: 'GET',
    text: '/api/v1/Document/GetSummary/01LVNLRU55BFFEPXJI3FA3QFUEYTJLAGSS',
  };

  const getSummaryResponse = [
    {
      className: 'textStyle1',
      text: '{',
    },
    {
      className: 'textStyle2',
      text: '"succeeded": true,',
    },
    {
      className: 'textStyle2',
      text: '"message": null,',
    },
    {
      className: 'textStyle2',
      text: '"errors": null,',
    },
    {
      className: 'textStyle2',
      text: '"data": {',
    },
    {
      className: 'textStyle4',
      text: '"id": "58",',
    },
    {
      className: 'textStyle4',
      text: '"title": "Corporations Act 2001 Vol 2",',
    },
    {
      className: 'textStyle4',
      text: '"clientName": "RegulationCity",',
    },
    {
      className: 'textStyle4',
      text: '"documentId": "01LVNLRU55BFFEPXJI3FA3QFUEYTJLAGSS",',
    },
    {
      className: 'textStyle4',
      text: '"documentName": "Corporations Act 2001 Vol 2",',
    },
    {
      className: 'textStyle4',
      text: '"status": "Published",',
    },
    {
      className: 'textStyle4',
      text: '"summary": "<div><p>Document Registration Date: 16 September 2023<br>...</p></div>"',
    },
    {
      className: 'textStyle2',
      text: '}',
    },
    {
      className: 'textStyle1',
      text: '}',
    },
  ];

  const getSummaryResponseCopy = `
  {
    "succeeded": true,
    "message": null,
    "errors": null,
    "data": {
      "id": "58",
      "title": "Corporations Act 2001 Vol 2",
      "clientName": "RegulationCity",
      "documentId": "01LVNLRU55BFFEPXJI3FA3QFUEYTJLAGSS",
      "documentName": "Corporations Act 2001 Vol 2",
      "status": "Published",
      "summary": "<div><p>Document Registration Date: 16 September 2023<br>...</p></div>"
    }
    // A document with no summary returns "data": null
  }
  `;

  return [
    {
      title: 'Introduction',
      section: false,
      content: (
        <div className='d-flex flex-column'>
          <p>
            Welcome to the RegulationCity API — the developer interface to the
            same always-current regulatory content that powers RegulationCity,
            maintained by Revealr and delivered as clean, structured JSON.
          </p>
          <p>
            This is a RESTful API. Whether you're building a GRC platform, a
            RegTech product, or compliance tooling for an in-house team, you can
            pull documents, drill into individual sections, resolve glossary
            terms, follow related content, search the full corpus, and navigate
            the taxonomy — all from one versioned API.
          </p>

          <h3>Key Endpoints</h3>
          <ul>
            <li>
              <b>Get Documents:</b> Retrieve a list of documents, including
              their titles, IDs, and other relevant information.
            </li>
            <li>
              <b>Get Document by ID:</b> Access detailed information about a
              specific document using its unique identifier.
            </li>
            <li>
              <b>Get Sections of a Document by ID:</b> Explore the sections
              within a document, including their titles and content.
            </li>
            <li>
              <b>Get Related Documents by ID:</b> Discover related documents
              associated with a specific document.
            </li>
            <li>
              <b>Get Glossary of a Document by ID:</b> Retrieve a document's
              glossary, which provides definitions for key terms used within the
              document.
            </li>
            <li>
              <b>Get Section by ID:</b> Access specific sections within
              documents using their unique identifiers.
            </li>
            <li>
              <b>Get Related Sections by ID:</b> Retrieve related sections
              associated with a specific section.
            </li>
            <li>
              <b>Get Summary by ID:</b> Retrieve a document's AI-generated
              summary.
            </li>
            <li>
              <b>Find by Title:</b> Look up documents, sections or glossary
              terms by a case-insensitive title match.
            </li>
            <li>
              <b>Full-Text Search:</b> Search across all content (titles and
              body) for a term and get back matching, highlighted sections.
            </li>
            <li>
              <b>Taxonomy:</b> Browse the four taxonomies (Regulator, Regulation
              Category, Entity Type, Document Type) and the documents tagged with
              each item.
            </li>
            <li>
              <b>AI Query:</b> Ask a natural-language question about one or more
              documents and get an AI-generated answer.
            </li>
          </ul>
          <p>
            Every endpoint is documented with example requests, responses, and
            parameter details. Each one returns the same response envelope — see{' '}
            <b>Response format</b> — and you can try any of them live, with no
            API key, in the <b>API Explorer</b>.
          </p>
        </div>
      ),
    },
    {
      title: 'Getting Started',
      section: false,
      content: (
        <div className='d-flex flex-column'>
          <p>
            Welcome to the Getting Started guide for our API. This section will
            walk you through the initial steps to start using our API
            effectively. By the end of this guide, you'll be ready to make your
            first API requests and access valuable data and resources.
          </p>
          <h3>Prerequisites</h3>
          <p>
            Before you begin, ensure that you have the following prerequisites
            in place:
          </p>
          <ul>
            <li>
              <b>A way to authenticate:</b> while you're signed in, the API
              Explorer authenticates from your session — no key required. To
              call the API from your own code, create an API key &amp; secret
              (available on every plan, including the Trial) — see{' '}
              <b>Authentication</b>.
            </li>
            <li>
              <b>HTTP Client:</b> You can use any HTTP client or programming
              language of your choice to make API requests. Popular options
              include cURL, Python, JavaScript (using libraries like Axios or
              Fetch), and many more.
            </li>
            <li>
              <b>API Documentation:</b> Familiarize yourself with our API
              documentation. You're already here, so you're off to a great
              start! This documentation provides detailed information about
              available endpoints, request parameters, and response formats.
            </li>
          </ul>
          <h3>Making Your First Request</h3>
          <p>
            Let's get started with a simple API request to retrieve a list of
            documents. Here's a basic example using cURL:
          </p>
          <CodeBox
            isArray={false}
            wrapperClass='d-flex flex-row align-items-center'
            insideNumber={getDocsEndpoint.number}
            insideRequest={getDocsEndpoint.request}
            insideText={getDocsEndpoint.text}
            handleCopy={() => copyText(getDocsEndpoint.text)}
          />
          <h3 className='mt-3'>Understanding the Response</h3>
          <p>
            A successful request returns a JSON envelope with a{' '}
            <code>succeeded</code> flag and your payload under <code>data</code>{' '}
            (see the <b>Response format</b> page for the full shape).
          </p>
          <p>Here's an example response for the "Get Documents" endpoint:</p>
          <CodeBox
            isArray={true}
            arrayToMap={getDocsResponse}
            wrapperClass='d-flex flex-row align-items-start'
            handleCopy={() => copyText(getDocsResponseCopy)}
          />
          <h3 className='mt-3'>Exploring Endpoints</h3>
          <p>
            Now that you've made your first request, you can explore various
            endpoints in our API to access documents, sections, glossaries,
            related content, and more. Refer to the API Documentation for a
            comprehensive list of available endpoints, their purposes, and
            usage.
          </p>
          <h3>Next Steps</h3>
          <p>
            From here, explore the endpoint reference, try requests live in the
            API Explorer, and integrate the data into your application. When
            you're ready to move from evaluating to integrating at scale, see
            the <b>Plans</b> page to talk to us about the <b>Enterprise</b>{' '}
            plan.
          </p>
        </div>
      ),
    },
    {
      title: 'Authentication',
      section: false,
      content: (
        <div className='d-flex flex-column'>
          <p>
            How you authenticate depends on how you're calling the API. There
            are two ways, matching the two ways you can use the platform.
          </p>
          <h3>In the browser — API Explorer</h3>
          <p>
            While you're signed in to the portal, the <b>API Explorer</b>{' '}
            authenticates your requests automatically using your session —
            there's nothing to set up and <b>no API key required</b>. This is
            the quickest way to evaluate the API and try endpoints against live
            data.
          </p>
          <h3>From your own code — API keys</h3>
          <p>
            To call the API programmatically from your own server or
            application, you authenticate with an <b>API key and secret</b>.
            Keys are available on every plan — including the <b>Trial</b> — so
            you can integrate straight away.
          </p>
          <ol>
            <li>
              Generate your key: on the <b>API Keys</b> page, create a new key.
              Your key and secret are shown once, at creation — copy the secret
              then, as it can't be retrieved again.
            </li>
            <li>
              Include the key and secret in your requests: send them in the{' '}
              <code>X-Api-Key</code> and <code>X-Api-Secret</code> headers of
              each HTTP request. Here's an example using cURL:
            </li>
          </ol>
          <CodeBox
            isArray={false}
            lang='cURL'
            wrapperClass='d-flex flex-row align-items-center'
            insideText='curl -H "X-Api-Key: YOUR_API_KEY" -H "X-Api-Secret: YOUR_API_SECRET" https://webapi.regulationcity.com/api/v1/Document/Get'
            handleCopy={() =>
              copyText(
                'curl -H "X-Api-Key: YOUR_API_KEY" -H "X-Api-Secret: YOUR_API_SECRET" https://webapi.regulationcity.com/api/v1/Document/Get'
              )
            }
          />
          <p>
            Replace YOUR_API_KEY and YOUR_API_SECRET with the values from your
            generated key.
          </p>
          <h3>Security Recommendations</h3>
          <p>
            To ensure the security of your API key and data, consider the
            following recommendations:
          </p>
          <ul>
            <li>
              Keep Your API Key Secure: Treat your API key as sensitive
              information. Do not share it publicly, commit it to version
              control, or expose it in client-side code.
            </li>
            <li>
              Use HTTPS: Always make API requests using the HTTPS protocol to
              encrypt data in transit and protect your API key from
              interception.
            </li>
            <li>
              Token Expiry and Rotation: Periodically rotate your API keys and
              follow best practices for key management. If you suspect your API
              key has been compromised, contact us immediately to request a new
              key.
            </li>
            <li>
              Access Control: Limit access to your API key to only those
              individuals or systems that require it. Follow the principle of
              least privilege.
            </li>
          </ul>
          <h3>Usage Limits</h3>
          <p>
            The <b>Trial</b> plan includes a fixed allowance of 100 API calls —
            enough to evaluate and prototype against live data. It's a one-time
            total, not a monthly reset. Calls made from the API Explorer count
            towards the same allowance.
          </p>
          <p>
            If you reach your allowance, further calls return a{' '}
            <code>429 Too Many Requests</code> response. Need more? Contact us
            and we'll add calls to your account, or move you to the Enterprise
            plan (uncapped, priced to your usage) — see the Plans page to get
            in touch.
          </p>
          <p>
            You can track your usage at any time on the <b>Usage</b> page in
            the portal.
          </p>
          <h3>Testing Your Authentication</h3>
          <p>
            You can test your authentication by making a simple request to one
            of our API endpoints, such as the "Get Documents" endpoint. If your
            authentication is successful, you will receive a valid response.
          </p>
          <h3>Troubleshooting</h3>
          <p>
            If you encounter any issues with authentication or have questions
            about securing your API key, get in touch via the{' '}
            <b>Contact Us</b> option on the Plans page and we'll help you
            sort it out.
          </p>
        </div>
      ),
    },
    {
      title: 'Response format',
      section: false,
      content: (
        <div className='d-flex flex-column'>
          <p className='p-0 m-0'>
            Endpoints return a consistent response envelope, so you handle one
            shape across the API. The HTTP status code carries success/failure;
            the body wraps the payload.
          </p>
          <CodeBox
            isArray={true}
            arrayToMap={[
              { className: 'textStyle1', text: '{' },
              {
                className: 'textStyle2',
                text: '"succeeded": true,   // whether the request succeeded',
              },
              {
                className: 'textStyle2',
                text: '"message": null,     // human-readable note (usually null on success)',
              },
              {
                className: 'textStyle2',
                text: '"errors": null,      // validation errors when a request is rejected',
              },
              {
                className: 'textStyle2',
                text: '"data": [ ... ]      // the payload (object, array or map)',
              },
              { className: 'textStyle1', text: '}' },
            ]}
            wrapperClass='d-flex flex-row align-items-start'
            showCopyButton={false}
          />
          <h3 className='mt-3'>Fields</h3>
          <ul className='mt-3'>
            <li>
              <b>succeeded</b> — <code>true</code> when the request succeeded.
            </li>
            <li>
              <b>message</b> — a human-readable note; <code>null</code> on a
              normal success.
            </li>
            <li>
              <b>errors</b> — validation messages when a request is rejected;
              otherwise <code>null</code>.
            </li>
            <li>
              <b>data</b> — the payload: an object, array or map depending on the
              endpoint. A query that matches nothing returns an empty{' '}
              <code>data</code> (e.g. <code>[]</code>), not an error.
            </li>
          </ul>
          <p>
            The <code>version</code> path segment (e.g. <code>v1</code>) is
            required on every route. Two AI endpoints — <b>Query a Document</b>{' '}
            and <b>Compare Documents</b> — return their AI service's own response
            shape rather than this envelope; each is documented on its own page.
          </p>
        </div>
      ),
    },
    {
      title: 'GET Documents',
      content: (
        <div className='d-flex flex-column'>
          <p className='p-0 m-0'>Retrieve a list of documents.</p>
          <EndpointBar
            isArray={false}
            wrapperClass='d-flex flex-row align-items-center'
            insideNumber={getDocsEndpoint.number}
            insideRequest={getDocsEndpoint.request}
            insideText={getDocsEndpoint.text}
            handleCopy={() => copyText(getDocsEndpoint.text)}
          />
          <h3 className='mt-3'>Parameters</h3>
          <ul className='mt-3'>
            <li>
              <b>version (required) -</b> The API version number.
            </li>
          </ul>
          <h3 className='mt-3'>Request</h3>
          <CodeBox
            isArray={false}
            wrapperClass='d-flex flex-row align-items-center'
            insideNumber={getDocsRequest.number}
            insideRequest={getDocsRequest.request}
            insideText={getDocsRequest.text}
            handleCopy={() => copyText(getDocsRequest.text)}
          />
          <h3 className='mt-3'>Response</h3>
          <CodeBox
            isArray={true}
            arrayToMap={getDocsResponse}
            wrapperClass='d-flex flex-row align-items-start'
            handleCopy={() => copyText(getDocsResponseCopy)}
          />
        </div>
      ),
    },
    {
      title: 'GET Documents by Title',
      content: (
        <EndpointSection
          description='Search for documents whose title contains the given text (case-insensitive). Returns the same document objects as "GET Documents".'
          endpoint='/api/v{version}/Document/GetByTitle?title={title}'
          params={[
            versionParam,
            {
              name: 'title',
              required: true,
              desc: 'The text to match against document titles (case-insensitive "contains" match).',
            },
          ]}
          example='/api/v1/Document/GetByTitle?title=takeover'
          responseJson={`{
  "succeeded": true,
  "message": null,
  "errors": null,
  "data": [
    {
      "id": "01LVNLRUZ2V4IOEIHYCJDIR3NRIVWLW3RK",
      "title": "RG 62 - Better disclosure for investors",
      "status": "Latest",
      "versionNumber": "-",
      "versionDate": "2000-07-31T14:00:00Z",
      "registrationDate": null,
      "taxonomy": {
        "regulator": { "id": "1", "title": "ASIC" },
        "regulation-category": { "id": "14", "title": "Regulatory Guides" },
        "entity-type": null,
        "document-type": null
      }
    }
    // ... other matching documents ...
  ]
}`}
        />
      ),
    },
    {
      title: 'GET Document by ID',
      content: (
        <div className='d-flex flex-column'>
          <p className='p-0 m-0'>
            Retrieves a document object containing detailed information about a
            specific regulatory document. This endpoint allows you to access
            crucial attributes such as document title, version date, relevant
            regulators, and more. Whether you're building compliance
            applications, conducting regulatory research, or maintaining
            up-to-date documentation, this API call provides you with the
            necessary data to streamline your processes.
          </p>
          <EndpointBar
            isArray={false}
            wrapperClass='d-flex flex-row align-items-center'
            insideNumber={getDocIdEndpoint.number}
            insideRequest={getDocIdEndpoint.request}
            insideText={getDocIdEndpoint.text}
            handleCopy={() => copyText(getDocIdEndpoint.text)}
          />
          <h3 className='mt-3'>Parameters</h3>
          <ul className='mt-3'>
            <li>
              <b>version (required) -</b> The version number of the API.
            </li>
            <li>
              <b>id (required) -</b> The unique ID of the document.
            </li>
          </ul>
          <h3 className='mt-3'>Request</h3>
          <CodeBox
            isArray={false}
            wrapperClass='d-flex flex-row align-items-center'
            insideNumber={getDocIdRequest.number}
            insideRequest={getDocIdRequest.request}
            insideText={getDocIdRequest.text}
            handleCopy={() => copyText(getDocIdRequest.text)}
          />
          <h3 className='mt-3'>Response</h3>
          <CodeBox
            isArray={true}
            arrayToMap={getDocIdResponse}
            wrapperClass='d-flex flex-row align-items-start'
            handleCopy={() => copyText(getDocIdResponseCopy)}
          />
        </div>
      ),
    },
    {
      title: 'GET Sections of a Document by ID',
      content: (
        <div className='d-flex flex-column'>
          <p className='p-0 m-0'>
            Retrieve a document's sections (its structure). This returns each
            section's metadata but not its body — fetch a section's full content
            with <b>Get Section by ID</b>.
          </p>
          <EndpointBar
            isArray={false}
            wrapperClass='d-flex flex-row align-items-center'
            insideNumber={getSectionsEndpoint.number}
            insideRequest={getSectionsEndpoint.request}
            insideText={getSectionsEndpoint.text}
            handleCopy={() => copyText(getSectionsEndpoint.text)}
          />
          <h3 className='mt-3'>Parameters</h3>
          <ul className='mt-3'>
            <li>
              <b>version (required) -</b> The version number of the API.
            </li>
            <li>
              <b>id (required) -</b> The unique ID of the document.
            </li>
          </ul>
          <h3 className='mt-3'>Request</h3>
          <CodeBox
            isArray={false}
            wrapperClass='d-flex flex-row align-items-center'
            insideNumber={getSectionsRequest.number}
            insideRequest={getSectionsRequest.request}
            insideText={getSectionsRequest.text}
            handleCopy={() => copyText(getSectionsRequest.text)}
          />
          <h3 className='mt-3'>Response</h3>
          <CodeBox
            isArray={true}
            arrayToMap={getSectionsResponse}
            wrapperClass='d-flex flex-row align-items-start'
            handleCopy={() => copyText(getSectionsResponseCopy)}
          />
        </div>
      ),
    },
    {
      title: 'GET Related Documents by ID',
      content: (
        <div className='d-flex flex-column'>
          <p className='p-0 m-0'>
            Retrieves related documents associated with a specific document
            based on its unique ID
          </p>
          <EndpointBar
            isArray={false}
            wrapperClass='d-flex flex-row align-items-center'
            insideNumber={getRelatedDocsEndpoint.number}
            insideRequest={getRelatedDocsEndpoint.request}
            insideText={getRelatedDocsEndpoint.text}
            handleCopy={() => copyText(getRelatedDocsEndpoint.text)}
          />
          <h3 className='mt-3'>Parameters</h3>
          <ul className='mt-3'>
            <li>
              <b>version (required) -</b> The version number of the API.
            </li>
            <li>
              <b>id (required) -</b> The unique ID of the document.
            </li>
          </ul>
          <h3 className='mt-3'>Request</h3>
          <CodeBox
            isArray={false}
            wrapperClass='d-flex flex-row align-items-center'
            insideNumber={getRelatedDocsRequest.number}
            insideRequest={getRelatedDocsRequest.request}
            insideText={getRelatedDocsRequest.text}
            handleCopy={() => copyText(getRelatedDocsRequest.text)}
          />
          <h3 className='mt-3'>Response</h3>
          <CodeBox
            isArray={true}
            arrayToMap={getRelatedDocsResponse}
            wrapperClass='d-flex flex-row align-items-start'
            handleCopy={() => copyText(getRelatedDocsResponseCopy)}
          />
        </div>
      ),
    },
    {
      title: 'GET Glossary of a Document by ID',
      content: (
        <div className='d-flex flex-column'>
          <p className='p-0 m-0'>
            Retrieve a document's glossaries and their defined terms. A document
            with no glossary returns an empty list.
          </p>
          <EndpointBar
            isArray={false}
            wrapperClass='d-flex flex-row align-items-center'
            insideNumber={getGlossaryEndpoint.number}
            insideRequest={getGlossaryEndpoint.request}
            insideText={getGlossaryEndpoint.text}
            handleCopy={() => copyText(getGlossaryEndpoint.text)}
          />
          <h3 className='mt-3'>Parameters</h3>
          <ul className='mt-3'>
            <li>
              <b>version (required) -</b> The version number of the API.
            </li>
            <li>
              <b>id (required) -</b> The unique ID of the document.
            </li>
          </ul>
          <h3 className='mt-3'>Request</h3>
          <CodeBox
            isArray={false}
            wrapperClass='d-flex flex-row align-items-center'
            insideNumber={getGlossaryRequest.number}
            insideRequest={getGlossaryRequest.request}
            insideText={getGlossaryRequest.text}
            handleCopy={() => copyText(getGlossaryRequest.text)}
          />
          <h3 className='mt-3'>Response</h3>
          <CodeBox
            isArray={true}
            arrayToMap={getGlossaryResponse}
            wrapperClass='d-flex flex-row align-items-start'
            handleCopy={() => copyText(getGlossaryResponseCopy)}
          />
        </div>
      ),
    },
    {
      title: 'GET Glossary by Title',
      content: (
        <EndpointSection
          description='Search glossary terms across all documents by term name (case-insensitive). Returns a map of matching term → meaning.'
          endpoint='/api/v{version}/Document/GetGlossaryByTitle?title={title}'
          params={[
            versionParam,
            {
              name: 'title',
              required: true,
              desc: 'The text to match against glossary term names (case-insensitive "contains" match).',
            },
          ]}
          example='/api/v1/Document/GetGlossaryByTitle?title=APRA'
          responseJson={`{
  "succeeded": true,
  "message": null,
  "errors": null,
  "data": {
    "APRA": "means the Australian Prudential Regulation Authority."
    // ... other matching terms ...
  }
}`}
        />
      ),
    },
    {
      title: 'GET Summary by ID',
      content: (
        <div className='d-flex flex-column'>
          <p className='p-0 m-0'>
            Retrieve a document's AI-generated summary. Summaries exist for a
            curated subset of the corpus — a document without one returns{' '}
            <code>data: null</code>.
          </p>
          <EndpointBar
            isArray={false}
            wrapperClass='d-flex flex-row align-items-center'
            insideNumber={getSummaryEndpoint.number}
            insideRequest={getSummaryEndpoint.request}
            insideText={getSummaryEndpoint.text}
            handleCopy={() => copyText(getSummaryEndpoint.text)}
          />
          <h3 className='mt-3'>Parameters</h3>
          <ul className='mt-3'>
            <li>
              <b>version (required) -</b> The version number of the API.
            </li>
            <li>
              <b>id (required) -</b> The unique ID of the document.
            </li>
          </ul>
          <h3 className='mt-3'>Request</h3>
          <CodeBox
            isArray={false}
            wrapperClass='d-flex flex-row align-items-center'
            insideNumber={getSummaryRequest.number}
            insideRequest={getSummaryRequest.request}
            insideText={getSummaryRequest.text}
            handleCopy={() => copyText(getSummaryRequest.text)}
          />
          <h3 className='mt-3'>Response</h3>
          <CodeBox
            isArray={true}
            arrayToMap={getSummaryResponse}
            wrapperClass='d-flex flex-row align-items-start'
            handleCopy={() => copyText(getSummaryResponseCopy)}
          />
        </div>
      ),
    },
    {
      title: 'GET Section by ID',
      content: (
        <div className='d-flex flex-column'>
          <p className='p-0 m-0'>
            Retrieve a single section by its id, including its full body
            (<code>content</code>) — the one section endpoint that returns the
            section text.
          </p>
          <EndpointBar
            isArray={false}
            wrapperClass='d-flex flex-row align-items-center'
            insideNumber={getSectionIdEndpoint.number}
            insideRequest={getSectionIdEndpoint.request}
            insideText={getSectionIdEndpoint.text}
            handleCopy={() => copyText(getSectionIdEndpoint.text)}
          />
          <h3 className='mt-3'>Parameters</h3>
          <ul className='mt-3'>
            <li>
              <b>version (required) -</b> The version number of the API.
            </li>
            <li>
              <b>id (required) -</b> The unique ID of the document.
            </li>
          </ul>
          <h3 className='mt-3'>Request</h3>
          <CodeBox
            isArray={false}
            wrapperClass='d-flex flex-row align-items-center'
            insideNumber={getSectionIdRequest.number}
            insideRequest={getSectionIdRequest.request}
            insideText={getSectionIdRequest.text}
            handleCopy={() => copyText(getSectionIdRequest.text)}
          />
          <h3 className='mt-3'>Response</h3>
          <CodeBox
            isArray={true}
            arrayToMap={getSectionIdResponse}
            wrapperClass='d-flex flex-row align-items-start'
            handleCopy={() => copyText(getSectionIdResponseCopy)}
          />
        </div>
      ),
    },
    {
      title: 'GET Sections by Title',
      content: (
        <EndpointSection
          description='Find the sections of a document whose title contains the given text (case-insensitive). Scoped to one document — pass its id. Returns the same section objects as "GET Sections of a Document by ID" — structure only, no section body.'
          endpoint='/api/v{version}/Section/GetByTitle?documentId={documentId}&title={title}'
          params={[
            versionParam,
            {
              name: 'documentId',
              required: true,
              desc: 'The document to search within (a doc_id from Get Documents).',
            },
            {
              name: 'title',
              required: true,
              desc: 'The text to match against the document’s section titles (case-insensitive "contains" match).',
            },
          ]}
          example='/api/v1/Section/GetByTitle?documentId=01LVNLRUZ2V4IOEIHYCJDIR3NRIVWLW3RK&title=disclosure'
          responseJson={`{
  "succeeded": true,
  "message": null,
  "errors": null,
  "data": [
    {
      "id": "6841",
      "title": "Preventing selective disclosure",
      "documentId": "01LVNLRUZ2V4IOEIHYCJDIR3NRIVWLW3RK",
      "documentName": "RG 62 - Better disclosure for investors",
      "pageId": "0d2f7c11-2a9c-4c33-9d21-8b7e4d55aa10",
      "pageParentId": "bfd5f2d1-1f5b-4d0c-ae32-838a78589823",
      "tags": ""
    }
    // ... other matching sections in this document ...
  ]
}`}
        />
      ),
    },
    {
      title: 'GET Related Sections by ID',
      content: (
        <div className='d-flex flex-column'>
          <p className='p-0 m-0'>
            Retrieves related sections associated with a specific section based
            on its unique ID
          </p>
          <EndpointBar
            isArray={false}
            wrapperClass='d-flex flex-row align-items-center'
            insideNumber={getRelatedSectionsEndpoint.number}
            insideRequest={getRelatedSectionsEndpoint.request}
            insideText={getRelatedSectionsEndpoint.text}
            handleCopy={() => copyText(getRelatedSectionsEndpoint.text)}
          />
          <h3 className='mt-3'>Parameters</h3>
          <ul className='mt-3'>
            <li>
              <b>version (required) -</b> The version number of the API.
            </li>
            <li>
              <b>id (required) -</b> The unique ID of the section.
            </li>
          </ul>
          <h3 className='mt-3'>Request</h3>
          <CodeBox
            isArray={false}
            wrapperClass='d-flex flex-row align-items-center'
            insideNumber={getRelatedSectionsRequest.number}
            insideRequest={getRelatedSectionsRequest.request}
            insideText={getRelatedSectionsRequest.text}
            handleCopy={() => copyText(getRelatedSectionsRequest.text)}
          />
          <h3 className='mt-3'>Response</h3>
          <CodeBox
            isArray={true}
            arrayToMap={getRelatedSectionsResponse}
            wrapperClass='d-flex flex-row align-items-start'
            handleCopy={() => copyText(getRelatedSectionsResponseCopy)}
          />
        </div>
      ),
    },
    {
      title: 'Full-Text Search',
      content: (
        <EndpointSection
          description='Search across all content (titles and body) for a term and get back the matching sections, with the term highlighted in the content using <mark> tags. No matches returns an empty list.'
          endpoint='/api/v{version}/Search/Get?q={q}'
          params={[
            versionParam,
            {
              name: 'q',
              required: true,
              desc: 'The search term to match against content titles and body.',
            },
          ]}
          example='/api/v1/Search/Get?q=takeover'
          responseJson={`{
  "succeeded": true,
  "message": null,
  "errors": null,
  "data": [
    {
      "id": "6833",
      "title": "Overview",
      "documentId": "01LVNLRUZ2V4IOEIHYCJDIR3NRIVWLW3RK",
      "documentName": "RG 62 - Better disclosure for investors",
      "pageId": "6d00f22c-82f9-416e-9170-dcbc5f96f63b",
      "pageParentId": "",
      "tags": "exception, prohibition, takeover, voting power",
      "content": "Key points The <mark>takeover</mark> provisions in Ch 6 of the Corporations Act 2001 impose a general prohibition ...",
      "slug": "overview"
    }
    // ... other matches ...
  ]
}`}
        />
      ),
    },
    {
      title: 'Taxonomy',
      section: false,
      content: (
        <div className='d-flex flex-column'>
          <p>
            Every document is classified across four independent{' '}
            <b>taxonomies</b>. A taxonomy is a dimension of classification with
            its own set of <b>items</b>; a document carries one item from each
            taxonomy.
          </p>
          <ul>
            <li>
              <b>Regulator</b> — e.g. ASIC, APRA, AUSTRAC, RBA, ACCC, OAIC
            </li>
            <li>
              <b>Regulation Category</b> — e.g. Acts, Regulations, Regulatory
              Guides
            </li>
            <li>
              <b>Entity Type</b> — e.g. Licensing, Takeovers, Insolvency
            </li>
            <li>
              <b>Document Type</b> — e.g. Guidance, Prudential Standards
            </li>
          </ul>
          <p>
            The taxonomies are not a hierarchy — they are independent tags. Each
            is addressed by a stable <code>key</code> (e.g.{' '}
            <code>regulator</code>). Call <b>Get Taxonomies</b> first to discover
            them and their keys, then list a taxonomy's items, or the documents
            tagged with an item.
          </p>
        </div>
      ),
    },
    {
      title: 'GET Taxonomies',
      content: (
        <EndpointSection
          description="Lists the taxonomies documents are classified by — each with its key, display name and item count. The key is what you pass to the other taxonomy endpoints."
          endpoint='/api/v{version}/Taxonomies'
          params={[versionParam]}
          example='/api/v1/Taxonomies'
          responseJson={`{
  "succeeded": true,
  "message": null,
  "errors": null,
  "data": [
    { "key": "regulator",           "name": "Regulator",           "count": 6  },
    { "key": "regulation-category", "name": "Regulation Category", "count": 16 },
    { "key": "entity-type",         "name": "Entity Type",         "count": 54 },
    { "key": "document-type",       "name": "Document Type",       "count": 8  }
  ]
}`}
        />
      ),
    },
    {
      title: 'GET Taxonomy Items',
      content: (
        <EndpointSection
          description="Lists the items within a taxonomy (e.g. the regulators in the “regulator” taxonomy). Taxonomies are small, so the full set is returned — filter locally if you need to."
          endpoint='/api/v{version}/Taxonomies/{taxonomy}/items'
          params={[
            versionParam,
            {
              name: 'taxonomy',
              required: true,
              desc: 'The taxonomy key — one of regulator, regulation-category, entity-type, document-type (see Get Taxonomies).',
            },
          ]}
          example='/api/v1/Taxonomies/regulator/items'
          responseJson={`{
  "succeeded": true,
  "message": null,
  "errors": null,
  "data": [
    { "id": "1", "title": "ASIC" },
    { "id": "2", "title": "APRA" }
    // ... more items ...
  ]
}`}
        />
      ),
    },
    {
      title: 'GET Taxonomy Item by ID',
      content: (
        <EndpointSection
          description="Returns a single taxonomy item by its id — e.g. the regulator with id “1” (ASIC). Handy for resolving a stored item id to its title."
          endpoint='/api/v{version}/Taxonomies/{taxonomy}/items/{id}'
          params={[
            versionParam,
            {
              name: 'taxonomy',
              required: true,
              desc: 'The taxonomy key — one of regulator, regulation-category, entity-type, document-type (see Get Taxonomies).',
            },
            {
              name: 'id',
              required: true,
              desc: 'The id of the item within the taxonomy (see Get Taxonomy Items).',
            },
          ]}
          example='/api/v1/Taxonomies/regulator/items/1'
          responseJson={`{
  "succeeded": true,
  "message": null,
  "errors": null,
  "data": { "id": "1", "title": "ASIC" }
}`}
        />
      ),
    },
    {
      title: 'GET Documents for Taxonomy Item',
      content: (
        <EndpointSection
          description="Lists the documents tagged with a taxonomy item — e.g. every document under the regulator “ASIC”. Returns the same document objects as the Document endpoints."
          endpoint='/api/v{version}/Taxonomies/{taxonomy}/items/{id}/documents'
          params={[
            versionParam,
            {
              name: 'taxonomy',
              required: true,
              desc: 'The taxonomy key — one of regulator, regulation-category, entity-type, document-type (see Get Taxonomies).',
            },
            {
              name: 'id',
              required: true,
              desc: 'The id of the item whose documents you want (see Get Taxonomy Items).',
            },
          ]}
          example='/api/v1/Taxonomies/regulator/items/1/documents'
          responseJson={`{
  "succeeded": true,
  "message": null,
  "errors": null,
  "data": [
    {
      "id": "01LVNLRU6MDFMMSPRGWVA2SHUXOJGVY6FA",
      "title": "RG 6 - Takeovers: Exceptions to the General Prohibition",
      "status": "Superseded",
      "versionNumber": "-",
      "versionDate": "2020-10-21T13:00:00Z",
      "registrationDate": null,
      "taxonomy": {
        "regulator": { "id": "1", "title": "ASIC" },
        "regulation-category": { "id": "14", "title": "Regulatory Guides" },
        "entity-type": { "id": "4", "title": "Takeovers" },
        "document-type": null
      }
    }
    // ... more documents ...
  ]
}`}
        />
      ),
    },
    {
      title: 'POST Query a Document',
      content: (
        <div className='d-flex flex-column'>
          <p className='p-0 m-0'>
            Ask a natural-language question about one or more documents and get
            an AI-generated answer, grounded in the selected document(s). This is
            the same AI assistant that powers RegulationCity, exposed for your
            own integrations. Unlike the other endpoints, this is a{' '}
            <b>POST</b> and takes a JSON body.
          </p>
          <EndpointBar
            isArray={false}
            wrapperClass='d-flex flex-row align-items-center'
            insideRequest='POST'
            insideText='/api/v{version}/Document/Query'
            handleCopy={() => copyText('/api/v1/Document/Query')}
          />
          <h3 className='mt-3'>Request Body</h3>
          <p>Send a JSON body with the following fields:</p>
          <table className='param-table'>
            <thead>
              <tr>
                <th>Field</th>
                <th>Required</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <code>query</code>
                </td>
                <td>
                  <span className='param-table__req'>required</span>
                </td>
                <td>The natural-language question to ask (max 2000 characters).</td>
              </tr>
              <tr>
                <td>
                  <code>documentIds</code>
                </td>
                <td>
                  <span className='param-table__req'>required</span>
                </td>
                <td>
                  An array of one or more document ids to scope the question to
                  (use the <code>id</code> values returned by Get Documents). At
                  least one is required.
                </td>
              </tr>
            </tbody>
          </table>
          <CodeBox
            isArray={true}
            arrayToMap={jsonLines(`{
  "query": "What does this guide say about disclosure to investors?",
  "documentIds": ["01LVNLRUZ2V4IOEIHYCJDIR3NRIVWLW3RK"]
}`)}
            wrapperClass='d-flex flex-row align-items-start'
            handleCopy={() =>
              copyText(
                '{ "query": "What does this guide say about disclosure to investors?", "documentIds": ["01LVNLRUZ2V4IOEIHYCJDIR3NRIVWLW3RK"] }'
              )
            }
          />
          <h3 className='mt-3'>Request</h3>
          <CodeBox
            isArray={false}
            lang='cURL'
            wrapperClass='d-flex flex-row align-items-center'
            insideText={
              'curl -X POST "https://webapi.regulationcity.com/api/v1/Document/Query" -H "X-Api-Key: YOUR_API_KEY" -H "X-Api-Secret: YOUR_API_SECRET" -H "Content-Type: application/json" -d \'{"query":"What does this guide say about disclosure to investors?","documentIds":["01LVNLRUZ2V4IOEIHYCJDIR3NRIVWLW3RK"]}\''
            }
            handleCopy={() =>
              copyText(
                'curl -X POST "https://webapi.regulationcity.com/api/v1/Document/Query" -H "X-Api-Key: YOUR_API_KEY" -H "X-Api-Secret: YOUR_API_SECRET" -H "Content-Type: application/json" -d \'{"query":"What does this guide say about disclosure to investors?","documentIds":["01LVNLRUZ2V4IOEIHYCJDIR3NRIVWLW3RK"]}\''
              )
            }
          />
          <h3 className='mt-3'>Response</h3>
          <p className='p-0 m-0'>
            This endpoint returns the AI service's own response shape (not the
            standard envelope): the answer is in <code>result.response</code> as
            HTML, along with the documents it drew on and token usage.
          </p>
          <CodeBox
            isArray={true}
            arrayToMap={jsonLines(`{
  "status_code": 200,
  "status": "success",
  "message": "Query generated successfully.",
  "result": {
    "response": "<p>RG 62 sets out principles for better disclosure to investors — information should be worded and presented clearly, concisely and effectively so investors can make informed decisions ...</p>",
    "source_documents": { "01LVNLRUZ2V4IOEIHYCJDIR3NRIVWLW3RK": "RG 62 - Better disclosure for investors" },
    "title": ""
  },
  "total_tokens": 1405,
  "total_cost": 0.0026
}`)}
            wrapperClass='d-flex flex-row align-items-start'
            handleCopy={() => copyText('')}
          />
          <p className='mt-3'>
            <b>Note:</b> each query counts as one call against your plan
            allowance. If a question can't be answered from the selected
            documents, the answer will say so — try widening the{' '}
            <code>documentIds</code> or rephrasing the question.
          </p>
        </div>
      ),
    },
    {
      title: 'GET My AI Queries',
      content: (
        <EndpointSection
          description="Retrieve your most recent AI questions and answers (newest first), scoped to your account. This is read-only history — it does NOT count against your call allowance."
          endpoint='/api/v{version}/ApiQuery/my?take={take}'
          params={[
            versionParam,
            {
              name: 'take',
              required: false,
              desc: 'How many recent queries to return (default 20, maximum 100).',
            },
          ]}
          example='/api/v1/ApiQuery/my?take=20'
          responseJson={`{
  "succeeded": true,
  "message": null,
  "errors": null,
  "data": [
    {
      "id": 1006,
      "question": "What does this guide say about disclosure to investors?",
      "response": "{ ...the AI service's answer, as returned by Query a Document... }",
      "documentIds": ["01LVNLRUZ2V4IOEIHYCJDIR3NRIVWLW3RK"],
      "created": "2026-07-08T01:50:59.13Z"
    }
    // ... older queries ...
  ]
}`}
        />
      ),
    },
    {
      title: 'GET Related Content by Document',
      content: (
        <EndpointSection
          description="Returns the supplementary resources (PDFs, links) that apply to a document — those attached to the document itself, to any of its taxonomy items, and to the whole corpus. Each item's appliesTo tells you where it comes from (document, a taxonomy key like “regulator”, or global). The relationship lookups are resolved for you server-side."
          endpoint='/api/v{version}/RelatedContent/GetByDocument/{documentId}'
          params={[
            versionParam,
            {
              name: 'documentId',
              required: true,
              desc: 'The document id — returns the resources that apply to this document and its taxonomy items.',
            },
          ]}
          example='/api/v1/RelatedContent/GetByDocument/01LVNLRUZN3WUDRKXTL5AKXXZBE3MQHED3'
          responseJson={`{
  "succeeded": true,
  "message": null,
  "errors": null,
  "data": [
    {
      "id": "13",
      "title": "Detect and Report Cuckoo Smurfing June 2021",
      "contentType": "PDF",
      "url": "https://adminwordflow.sharepoint.com/sites/...",
      "appliesTo": "regulator"
    }
    // ... more resources ...
  ]
}`}
        />
      ),
    },
    {
      title: 'GET Related Content by Taxonomy Item',
      content: (
        <EndpointSection
          description="Returns the supplementary resources pinned to a single taxonomy item (e.g. everything attached to the regulator “ASIC”). Use this to list the resources for a category regardless of any one document."
          endpoint='/api/v{version}/RelatedContent/GetByTaxonomyItem?taxonomy={taxonomy}&itemId={itemId}'
          params={[
            versionParam,
            {
              name: 'taxonomy',
              required: true,
              desc: 'The taxonomy key — one of regulator, regulation-category, entity-type, document-type (see Get Taxonomies).',
            },
            {
              name: 'itemId',
              required: true,
              desc: 'The id of the item within that taxonomy (see Get Taxonomy Items).',
            },
          ]}
          example='/api/v1/RelatedContent/GetByTaxonomyItem?taxonomy=regulator&itemId=3'
          responseJson={`{
  "succeeded": true,
  "message": null,
  "errors": null,
  "data": [
    {
      "id": "18",
      "title": "Politically Exposed Persons",
      "contentType": "PDF",
      "url": "https://adminwordflow.sharepoint.com/sites/...",
      "appliesTo": "regulator"
    }
    // ... more resources ...
  ]
}`}
        />
      ),
    },
    {
      title: 'POST Compare Documents',
      content: (
        <div className='d-flex flex-column'>
          <p className='p-0 m-0'>
            AI-compare two versions of a document (V1 older, V2 newer) and get a
            plain-English summary of what changed plus a section-by-section diff.
            This is a <b>POST</b> and takes a JSON body. Comparisons run an AI job
            and can take a minute or two.
          </p>
          <EndpointBar
            isArray={false}
            wrapperClass='d-flex flex-row align-items-center'
            insideRequest='POST'
            insideText='/api/v{version}/Comparison/Compare'
            handleCopy={() => copyText('/api/v1/Comparison/Compare')}
          />
          <h3 className='mt-3'>Request Body</h3>
          <table className='param-table'>
            <thead>
              <tr>
                <th>Field</th>
                <th>Required</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <code>documentId1</code>
                </td>
                <td>
                  <span className='param-table__req'>required</span>
                </td>
                <td>Document id of the older version (V1).</td>
              </tr>
              <tr>
                <td>
                  <code>documentId2</code>
                </td>
                <td>
                  <span className='param-table__req'>required</span>
                </td>
                <td>Document id of the newer version (V2). Must differ from documentId1.</td>
              </tr>
              <tr>
                <td>
                  <code>versionDate1</code>
                </td>
                <td>
                  <span className='param-table__req'>required</span>
                </td>
                <td>V1's version_date (from Get Document by ID).</td>
              </tr>
              <tr>
                <td>
                  <code>versionDate2</code>
                </td>
                <td>
                  <span className='param-table__req'>required</span>
                </td>
                <td>V2's version_date (from Get Document by ID).</td>
              </tr>
            </tbody>
          </table>
          <CodeBox
            isArray={true}
            arrayToMap={jsonLines(`{
  "documentId1": "01LVNLRU53PMGI3ZRKNNEJJTFUY4RCICBM",
  "documentId2": "01LVNLRU55AUFYTZU7R5CY57BOAK23OQAE",
  "versionDate1": "2023-10-19T13:00:00Z",
  "versionDate2": "2025-02-20T13:00:00Z"
}`)}
            wrapperClass='d-flex flex-row align-items-start'
            handleCopy={() => copyText('')}
          />
          <h3 className='mt-3'>Response</h3>
          <p className='p-0 m-0'>
            When ready, the response includes a <code>summary</code> of changes
            and a <code>section_comparison</code> array (per-section differences
            with links to each version). If still processing you'll get{' '}
            <code>status: "pending"</code> and a <code>comparison_id</code> to
            poll with <b>GET Comparison Result</b>.
          </p>
          <CodeBox
            isArray={true}
            arrayToMap={jsonLines(`{
  "comparison_id": "1cb4e89d-b1e4-4507-890f-d04e5e8703f2",
  "status": "completed",
  "doc_v1": { "doc_id": "01LVNLRU53...", "title": "Financial Accountability Regime Act 2023", "version_date": "2023-10-19T13:00:00Z", "document_version": "1" },
  "doc_v2": { "doc_id": "01LVNLRU55...", "title": "Financial Accountability Regime Act 2023", "version_date": "2025-02-20T13:00:00Z", "document_version": "2" },
  "summary": [
    "The compilation number changed from 'Compilation No. 1' to 'Compilation No. 2'.",
    "Amendments now go up to 'Act No. 14, 2025' (was 'Act No. 76, 2023')."
  ],
  "section_comparison": [
    {
      "section_title": "Financial Accountability Regime Act 2023",
      "status": "modified",
      "differences": ["V1 had 'Compilation No. 1' and V2 has 'Compilation No. 2'.", "..."],
      "v1_page_url": "https://regulationcity.com/app/01LVNLRU53.../6881f9a2-...",
      "v2_page_url": "https://regulationcity.com/app/01LVNLRU55.../3b34dd50-...",
      "v1_content": "No. 67, 2023 ... Compilation No. 1 ...",
      "v2_content": "No. 67, 2023 ... Compilation No. 2 ..."
    }
  ]
}`)}
            wrapperClass='d-flex flex-row align-items-start'
            handleCopy={() => copyText('')}
          />
          <p className='mt-3'>
            <b>Note:</b> a comparison counts as one call against your plan
            allowance. Compare real version pairs (two versions of the same
            document) — get each document's <code>version_date</code> from{' '}
            <b>Get Document by ID</b>.
          </p>
        </div>
      ),
    },
    {
      title: 'GET Comparison Result',
      content: (
        <EndpointSection
          description="Poll for the result of a previously submitted comparison by its comparison_id (returned by Compare Documents while a comparison is still processing). Read-only — not counted against your call allowance."
          endpoint='/api/v{version}/Comparison/{comparisonId}'
          params={[
            versionParam,
            {
              name: 'comparisonId',
              required: true,
              desc: 'The comparison_id returned by POST Compare Documents.',
            },
          ]}
          example='/api/v1/Comparison/1cb4e89d-b1e4-4507-890f-d04e5e8703f2'
          responseJson={`{
  "comparison_id": "1cb4e89d-b1e4-4507-890f-d04e5e8703f2",
  "status": "completed",
  "summary": [ "..." ],
  "section_comparison": [ { "section_title": "...", "status": "modified", "differences": ["..."] } ]
}`}
        />
      ),
    },
  ];
};
